import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Observable, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { ClientType, Invoice } from 'src/app/api/models';
import { Permission } from 'src/app/api/models/permission';
import { ClientPeerService } from '../../service/client-peer.service';
import { EndCustomerPeerService } from '../../service/end-customer-peer.service';
import { InvoiceNotificationsService } from 'src/app/service/invoice-notifications.service';
import { InvoicePeerService } from '../../service/invoice-peer.service';
import { LegacyWidgetVisibilityService } from 'src/app/service/legacy-widget-visibility.service';
import { RealtimeEvent, RealtimeNotificationsService } from '../../service/realtime-notifications.service';
import { UserService } from 'src/app/service/user.service';

@Component({
  selector: 'app-invoice-page',
  templateUrl: './invoice-page.component.html',
  styleUrls: ['./invoice-page.component.scss'],
  providers: [
    InvoicePeerService,
    ClientPeerService,
    EndCustomerPeerService,
    InvoiceNotificationsService,
    LegacyWidgetVisibilityService,
  ]
})
export class InvoicePageComponent implements OnInit, OnDestroy {
  private readonly ngDestroy$ = new Subject();

  invoice: Invoice | null = null;
  ClientType = ClientType;
  readonly legacyWidgetsVisible$: Observable<boolean>;
  /** Gates the header's link to the console-svelte Invoice Documents page. */
  readonly canPreviewDocuments$: Observable<boolean>;

  private invoiceScopeSub: Subscription | null = null;
  private clientScopeSub: Subscription | null = null;
  private endCustomerScopeSub: Subscription | null = null;
  private currentInvoiceId: number | null = null;
  private currentClientId: number | null = null;
  private currentEndCustomerId: number | null = null;

  constructor(private invoicePeerService: InvoicePeerService,
    private clientPeerService: ClientPeerService,
    private endCustomerPeerService: EndCustomerPeerService,
    private invoiceNotificationsService: InvoiceNotificationsService,
    private legacyWidgetVisibility: LegacyWidgetVisibilityService,
    private route: ActivatedRoute,
    private realtimeNotifications: RealtimeNotificationsService,
    private userService: UserService) {
    this.legacyWidgetsVisible$ = this.legacyWidgetVisibility.visible$;
    this.canPreviewDocuments$ = this.userService.hasPermission(Permission.InvoiceDocsPreview);
  }

  setLegacyWidgetsVisible(visible: boolean): void {
    this.legacyWidgetVisibility.set(visible);
  }

  ngOnInit() {
    this.invoicePeerService.getInvoice().pipe(
      takeUntil(this.ngDestroy$)
    ).subscribe(invoice => {
      this.invoice = invoice;
      if (invoice) {
        this.clientPeerService.initialize(invoice.client_id);
        this.endCustomerPeerService.initialize(invoice.end_customer_id);
        this.invoiceNotificationsService.reset();
        // Every state: a hold stops money and the advance past financing,
        // but handlers have to see it from the first state on.
        this.invoiceNotificationsService.setFinancingHolds(invoice.financing_holds);
        this.refreshScopeSubscriptions(invoice);
      }
    });

    this.realtimeNotifications.events$
      .pipe(takeUntil(this.ngDestroy$))
      .subscribe((event) => this.handleEntityRefresh(event));

    this.route.paramMap.pipe(
      takeUntil(this.ngDestroy$)
    )
      .subscribe((params: ParamMap) => {
        const invoiceId = +params.get('invoiceId');
        this.invoicePeerService.initialize(invoiceId);
      });
  }

  ngOnDestroy() {
    this.invoiceScopeSub?.unsubscribe();
    this.clientScopeSub?.unsubscribe();
    this.endCustomerScopeSub?.unsubscribe();
    this.ngDestroy$.next(undefined);
    this.ngDestroy$.complete();
  }

  /**
   * Refresh peer-service caches for the entity-update events that the
   * invoice page itself owns the reaction for. Widgets subscribe to
   * the same `events$` independently for their own concerns.
   *
   * All three events re-read the invoice the same way, by its modern id: a
   * financing hold placed or released on the client, the end customer or
   * the invoice itself (e.g. from the Svelte client page) emits that
   * target's event, and the invoice's `financing_holds` drive the chips,
   * the holds panel and the payment and Accept gates. The re-read never
   * goes through the legacy invoice, which is null for an invoice the
   * legacy app cannot serve.
   */
  private handleEntityRefresh(event: RealtimeEvent): void {
    if (!this.invoice) {
      return;
    }
    switch (event.event_type) {
      case 'client_updated':
        if (event.client_id === this.invoice.client_id) {
          this.clientPeerService.refresh();
          this.invoicePeerService.initialize(this.invoice.id);
        }
        break;
      case 'end_customer_updated':
        if (event.end_customer_id === this.invoice.end_customer_id) {
          this.endCustomerPeerService.refresh();
          this.invoicePeerService.initialize(this.invoice.id);
        }
        break;
      case 'invoice_updated':
        if (event.invoice_id === this.invoice.id) {
          this.invoicePeerService.initialize(this.invoice.id);
        }
        break;
    }
  }

  private refreshScopeSubscriptions(invoice: Invoice): void {
    if (this.currentInvoiceId !== invoice.id) {
      this.invoiceScopeSub?.unsubscribe();
      this.invoiceScopeSub = this.realtimeNotifications.subscribeToInvoice(invoice.id);
      this.currentInvoiceId = invoice.id;
    }
    if (this.currentClientId !== invoice.client_id) {
      this.clientScopeSub?.unsubscribe();
      this.clientScopeSub = this.realtimeNotifications.subscribeToClient(invoice.client_id);
      this.currentClientId = invoice.client_id;
    }
    if (invoice.end_customer_id != null && this.currentEndCustomerId !== invoice.end_customer_id) {
      this.endCustomerScopeSub?.unsubscribe();
      this.endCustomerScopeSub = this.realtimeNotifications.subscribeToEndCustomer(invoice.end_customer_id);
      this.currentEndCustomerId = invoice.end_customer_id;
    } else if (invoice.end_customer_id == null) {
      this.endCustomerScopeSub?.unsubscribe();
      this.endCustomerScopeSub = null;
      this.currentEndCustomerId = null;
    }
  }
}
